import React, { useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { useField } from 'formik'
import { StyledDescription } from '../../../../components/form/styled'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { useCountries } from '../../../../utils/hooks/useCountries'

type RuleExclusionProps = {
  editable: boolean
  /** The saved values, shown in display mode */
  authorizedCountries: Array<{ label: string }>
  countryBlacklistRedirectUrl?: string | null
}

/** The geographic rule exclusion, kept discreet: a small link that opens the accepted countries and the redirect URL */
const RuleExclusion: React.FC<RuleExclusionProps> = ({
  editable,
  authorizedCountries,
  countryBlacklistRedirectUrl,
}) => {
  const t = useAppTranslations()
  const [open, setOpen] = useState(false)
  const { data: countryData } = useCountries()
  const [field] = useField<string[]>({ name: 'authorizedCountries' })
  const acceptedCount = Array.isArray(field.value) ? field.value.length : 0

  const countriesOptions = useMemo(
    () => countryData?.getCountries.map((country) => ({ value: country.code, label: country.label })) ?? [],
    [countryData]
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        data-testid="hide"
        size="small"
        variant="text"
        color="inherit"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
        startIcon={
          <ChevronRight
            sx={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 150ms' }}
            fontSize="small"
          />
        }
        sx={{ color: 'text.secondary', fontWeight: 400, textTransform: 'none', px: 0.5 }}
      >
        {t('adgroup.rulesExceptionsLabel')}
        <Box component="span" sx={{ ml: 0.75, opacity: 0.85 }}>
          ·{' '}
          {acceptedCount > 0
            ? t('adgroup.rulesExceptionsSummary', { count: acceptedCount })
            : t('adgroup.rulesExceptionsOff')}
        </Box>
      </Button>
      {open && (
        <Box sx={{ mt: 0.5, p: 2, borderRadius: 1, bgcolor: 'action.hover', maxWidth: 640 }}>
          <StyledDescription>{t('adgroup.rulesExceptions')}</StyledDescription>
          <TransformDisplayToEdition
            inputType={'select'}
            label="adgroup.geoTargeting.acceptedCountries"
            data-testid="acceptedCountries"
            name={'authorizedCountries'}
            text={
              authorizedCountries.map((country) => country.label).join('\n') || t('adgroup.geoTargeting.allCountries')
            }
            noneTranslation="adgroup.geoTargeting.allCountries"
            multiple={true}
            disableHoverEdition
            variant="outlined"
            editable={editable}
            options={countriesOptions}
          />
          <TransformDisplayToEdition
            inputType={'input'}
            type={'text'}
            data-testid="redirectUrl"
            name={'countryBlacklistRedirectUrl'}
            label={'adgroup.redirectUrl'}
            text={countryBlacklistRedirectUrl}
            multiline={true}
            disableHoverEdition
            variant="outlined"
            editable={editable}
          />
        </Box>
      )}
    </Box>
  )
}

export default RuleExclusion
